import { SalesSegment } from '../types/onboarding/OnboardStore';

/**
 * The payment methods of the product appendix of the merchant agreement. Each groups the Edge product codes that
 * implement it; a merchant may have several codes per method (e.g. two direct invoice products, or card via two acquirers).
 */
export type PaymentMethodId =
    | 'invoice14'
    | 'invoice30'
    | 'partPayment'
    | 'account'
    | 'campaign'
    | 'prepaid'
    | 'card'
    | 'directBank'
    | 'swish'
    | 'vipps'
    | 'mobilePay'
    | 'amex'
    | 'applePay'
    | 'payPal'
    | 'ideal'
    | 'dankort'
    | 'other';

export type PaymentMethodGroupId = 'walley' | 'debt';

export interface PaymentMethodGroup {
    id: PaymentMethodGroupId;
    label: string;
}

/** The two blocks of the price grid: Walley's own credit products, and the payments collected for the merchant. */
export const paymentMethodGroups: PaymentMethodGroup[] = [
    { id: 'walley', label: 'Walley payments' },
    { id: 'debt', label: 'Debt payments' },
];

export interface PaymentMethod {
    id: PaymentMethodId;
    /** The block of the price grid the method belongs to. */
    group: PaymentMethodGroupId;
    /** Label as the product appendix words it. */
    label: string;
    /** Sales segments the appendix offers the method in. */
    segments: SalesSegment[];
    /**
     * Markets (country codes) the method is offered in; every market when missing. A local wallet is only shown in its own.
     * Empty when the appendix no longer offers the method anywhere: it is then neither shown nor addable, only kept for a
     * merchant already set up with it.
     */
    markets?: string[];
    /** Whether recurring purchases can be priced separately for the method. */
    supportsRecurring: boolean;
    /** Product type preselected when a product code is added to the method. */
    defaultProductType: string;
    /**
     * Product types to choose between when a code is added. Most methods have exactly one Edge account type, so this is
     * only set for the methods that can be set up on more than one; without it the type is fixed to defaultProductType.
     */
    addableProductTypes?: string[];
    /** Whether each product code carries a validity period (valid from/to), as a campaign does. */
    hasValidityDates?: boolean;
    /**
     * The Edge product codes a merchant is regularly set up with for the method, offered as a starting point when a
     * merchant has no prices yet. Missing for methods without a regular setup (a campaign has its own codes).
     */
    standardProductCodes?: StandardProductCode[];
}

/** A product code of the regular setup of a payment method. */
export interface StandardProductCode {
    productCode: string;
    /** The Edge account type of the code; the method's default type when missing. */
    productType?: string;
    /** The markets (country codes) the code is set up in; every market when missing. */
    markets?: string[];
    /** The sales segments the code is set up in; every segment of the method when missing. */
    segments?: SalesSegment[];
}

const bothSegments: SalesSegment[] = ['B2C', 'B2B'];
const consumerOnly: SalesSegment[] = ['B2C'];

/** The markets where Walley's credit products are part of the regular setup. */
const creditMarkets = ['SE', 'NO', 'FI'];
/** The Nordic markets, where the Nordic wallets are offered. */
const nordicMarkets = ['SE', 'NO', 'FI', 'DK'];

/** The payment methods in the order of the product appendix. */
export const paymentMethods: PaymentMethod[] = [
    {
        id: 'invoice14',
        group: 'walley',
        label: 'Invoice 14 days',
        segments: consumerOnly,
        supportsRecurring: true,
        defaultProductType: 'DirectInvoiceAccount',
        standardProductCodes: [{ productCode: 'DI_001', markets: creditMarkets }],
    },
    {
        id: 'invoice30',
        group: 'walley',
        label: 'Invoice 30 days',
        segments: bothSegments,
        supportsRecurring: true,
        defaultProductType: 'DirectInvoiceAccount',
        standardProductCodes: [
            // Companies are invoiced on DI_001 with 30 days; consumers get a 30-day invoice on DI_002, next to the 14-day one.
            { productCode: 'DI_001', segments: ['B2B'], markets: creditMarkets },
            { productCode: 'DI_002', segments: ['B2C'], markets: creditMarkets },
        ],
    },
    {
        id: 'partPayment',
        group: 'walley',
        label: 'Partial payment',
        segments: consumerOnly,
        supportsRecurring: false,
        defaultProductType: 'InterestFreeAccount',
        addableProductTypes: ['InterestFreeAccount', 'AnnuityAccount'],
        standardProductCodes: [
            { productCode: 'IF_3_001', markets: creditMarkets },
            { productCode: 'IF_6_001', markets: creditMarkets },
            { productCode: 'IF_12_001', markets: creditMarkets },
            { productCode: 'AN_24_001', productType: 'AnnuityAccount', markets: creditMarkets },
            { productCode: 'AN_36_001', productType: 'AnnuityAccount', markets: creditMarkets },
        ],
    },
    {
        id: 'account',
        group: 'walley',
        label: 'Account',
        segments: consumerOnly,
        supportsRecurring: false,
        defaultProductType: 'CreditAccount',
        standardProductCodes: [{ productCode: 'CA_001', markets: creditMarkets }],
    },
    {
        id: 'campaign',
        group: 'walley',
        label: 'Campaign',
        segments: consumerOnly,
        supportsRecurring: false,
        defaultProductType: 'CreditAccount',
        addableProductTypes: ['CreditAccount', 'DirectInvoiceAccount'],
        hasValidityDates: true,
    },
    {
        id: 'prepaid',
        group: 'walley',
        label: 'Prepaid',
        segments: bothSegments,
        supportsRecurring: false,
        defaultProductType: 'DirectInvoiceAccount',
        standardProductCodes: [{ productCode: 'PREPAID', markets: creditMarkets }],
    },
    {
        id: 'card',
        group: 'debt',
        label: 'Card payment',
        segments: bothSegments,
        supportsRecurring: true,
        defaultProductType: 'CardAccount',
        standardProductCodes: [{ productCode: 'VisaMcViaAdyen' }, { productCode: 'Bambora' }],
    },
    {
        id: 'directBank',
        group: 'debt',
        label: 'Direct bank payment',
        segments: consumerOnly,
        supportsRecurring: false,
        defaultProductType: 'TrustlyAccount',
        standardProductCodes: [{ productCode: 'Trustly' }],
    },
    {
        id: 'swish',
        group: 'debt',
        label: 'Swish',
        segments: consumerOnly,
        markets: ['SE'],
        supportsRecurring: true,
        defaultProductType: 'SwishAccount',
        addableProductTypes: ['SwishAccount', 'BrokeredAccount'],
        standardProductCodes: [{ productCode: 'Swish', markets: ['SE'] }],
    },
    {
        id: 'vipps',
        group: 'debt',
        label: 'Vipps',
        segments: consumerOnly,
        markets: nordicMarkets,
        supportsRecurring: false,
        defaultProductType: 'CardAccount',
        addableProductTypes: ['CardAccount', 'BrokeredAccount'],
        standardProductCodes: [{ productCode: 'Vipps', markets: ['NO'] }],
    },
    {
        id: 'mobilePay',
        group: 'debt',
        label: 'MobilePay',
        segments: consumerOnly,
        markets: nordicMarkets,
        supportsRecurring: false,
        defaultProductType: 'CardAccount',
        addableProductTypes: ['CardAccount', 'BrokeredAccount'],
        standardProductCodes: [{ productCode: 'MobilePay', markets: ['DK', 'FI'] }],
    },
    {
        id: 'amex',
        group: 'debt',
        label: 'American Express',
        segments: consumerOnly,
        // No longer offered, so only shown for a merchant that already has an Amex code.
        markets: [],
        supportsRecurring: false,
        defaultProductType: 'BrokeredAccount',
    },
    {
        id: 'applePay',
        group: 'debt',
        label: 'Apple Pay',
        segments: consumerOnly,
        supportsRecurring: true,
        defaultProductType: 'CardAccount',
        standardProductCodes: [{ productCode: 'ApplePay' }],
    },
    {
        id: 'payPal',
        group: 'debt',
        label: 'PayPal',
        segments: consumerOnly,
        supportsRecurring: false,
        defaultProductType: 'BrokeredAccount',
        addableProductTypes: ['CardAccount', 'BrokeredAccount'],
        standardProductCodes: [{ productCode: 'PayPal' }],
    },
    {
        id: 'ideal',
        group: 'debt',
        label: 'iDEAL',
        segments: consumerOnly,
        markets: ['NL'],
        supportsRecurring: false,
        defaultProductType: 'CardAccount',
        standardProductCodes: [{ productCode: 'IdealViaAdyen', markets: ['NL'] }],
    },
    {
        id: 'dankort',
        group: 'debt',
        label: 'Dankort',
        segments: consumerOnly,
        markets: ['DK'],
        supportsRecurring: false,
        // Not part of the regular setup, so it has no standard code.
        defaultProductType: 'BrokeredAccount',
    },
    {
        // Fallback for codes the catalog cannot place; only shown when a merchant has such a code.
        id: 'other',
        group: 'debt',
        label: 'Other',
        segments: consumerOnly,
        supportsRecurring: false,
        defaultProductType: 'CardAccount',
    },
];

export const getPaymentMethod = (id: PaymentMethodId): PaymentMethod =>
    paymentMethods.find(method => method.id === id) ?? paymentMethods[paymentMethods.length - 1];

/** The payment methods the product appendix offers in the given sales segment, in appendix order. */
export const paymentMethodsForSegment = (segment: SalesSegment): PaymentMethod[] =>
    paymentMethods.filter(method => method.segments.includes(segment));

/** Whether the product appendix offers the method in the given market: its block is offered there, and so is the method. */
export const isOfferedInMarket = (method: PaymentMethod, country: PriceCountry): boolean =>
    country.groups.includes(method.group) &&
    (!method.markets || method.markets.includes(country.code));

/** The codes of the regular setup of the method in the given market and segment, each with its account type. */
export const standardProductCodesIn = (
    method: PaymentMethod,
    countryCode: string,
    segment: SalesSegment
): { productCode: string; productType: string }[] =>
    (method.standardProductCodes ?? [])
        .filter(code => !code.markets || code.markets.includes(countryCode))
        .filter(code => !code.segments || code.segments.includes(segment))
        .map(code => ({
            productCode: code.productCode,
            productType: code.productType ?? method.defaultProductType,
        }));

/** The product type preselected when a code is added to the method: its default when offered, else the first choice. */
export const initialAddableProductType = (method: PaymentMethod): string =>
    !method.addableProductTypes || method.addableProductTypes.includes(method.defaultProductType)
        ? method.defaultProductType
        : method.addableProductTypes[0];

/** The method id from the API as the catalog knows it; anything unknown goes to Other. The API owns the classification. */
export const toPaymentMethodId = (paymentMethod: string): PaymentMethodId => {
    const id = (paymentMethod ?? '').trim();

    return (
        paymentMethods.find(method => method.id.toLowerCase() === id.toLowerCase())?.id ?? 'other'
    );
};

export interface PriceCountry {
    code: string;
    /** Name as the product appendix words it. */
    name: string;
    /** Settlement currency of stores in the country. */
    currency: string;
    /** Sales segments the product appendix offers in the country; corporate customers only in the Nordics. */
    segments: SalesSegment[];
    /** The blocks of the price grid offered in the country; Walley's own credit products only in the Nordics. */
    groups: PaymentMethodGroupId[];
}

const allGroups: PaymentMethodGroupId[] = ['walley', 'debt'];
const debtOnly: PaymentMethodGroupId[] = ['debt'];

/** The countries of the product appendix, in its order, with the EU catch-all last. */
export const priceCountries: PriceCountry[] = [
    { code: 'SE', name: 'Sweden', currency: 'SEK', segments: bothSegments, groups: allGroups },
    { code: 'NO', name: 'Norway', currency: 'NOK', segments: bothSegments, groups: allGroups },
    { code: 'FI', name: 'Finland', currency: 'EUR', segments: bothSegments, groups: allGroups },
    { code: 'DK', name: 'Denmark', currency: 'DKK', segments: bothSegments, groups: allGroups },
    { code: 'FR', name: 'France', currency: 'EUR', segments: consumerOnly, groups: debtOnly },
    { code: 'DE', name: 'Germany', currency: 'EUR', segments: consumerOnly, groups: debtOnly },
    { code: 'AT', name: 'Austria', currency: 'EUR', segments: consumerOnly, groups: debtOnly },
    { code: 'NL', name: 'Netherlands', currency: 'EUR', segments: consumerOnly, groups: debtOnly },
    {
        code: 'EU',
        name: 'Other EU countries',
        currency: 'EUR',
        segments: consumerOnly,
        groups: debtOnly,
    },
];

export const getPriceCountry = (countryCode: string): PriceCountry => {
    const code = (countryCode ?? '').trim().toUpperCase();

    return (
        priceCountries.find(country => country.code === code) ?? {
            code,
            name: code,
            currency: 'EUR',
            segments: consumerOnly,
            groups: debtOnly,
        }
    );
};
